<script lang="ts" setup>
import type { NuxtError } from '#app'
import { useModal } from 'vue-final-modal'
import type { Head } from 'zhead'

import ProductCard from '~/components/cards/ProductCard.vue'
import BasicButton from '~/components/form-elements/BasicButton.vue'
import ShareLink from '~/components/modal-windows/ShareLink.vue'
import { useSharing } from '~/composables/sharing'
import constants from '~/constants'
import ImageType from '~/enums/image-type'
import Page from '~/enums/page'
import ProductSort from '~/enums/product-sort'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import { useAuthStore } from '~/store/auth'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'
import type { ProductData } from '~/types/products'

const breadcrumbsItems: BreadcrumbsItem[] = [
  { name: 'Главная', path: Page.Main },
  { name: 'Акции', path: Page.SpecialOffers }
]
const imageTypes = [ImageType.Thumbnail480p, ImageType.Thumbnail360p, ImageType.Thumbnail240p, ImageType.Original]
const previewTypes = [ImageType.Thumbnail240p, ImageType.Original]

const { $apiHelper } = useNuxtApp()
const route = useRoute()
const authStore = useAuthStore()
const { copyLink, parsePromoCodeFromQuery } = useSharing()

const actualFullPath = ref<string | null>(null)

const descriptionHtml = computed(() => specialOfferData.value.attributes.descriptionHtml)
const imageUrl = computed(() => {
  const imageUrls = helpers.getImageUrls(specialOfferData.value.relationships.images.data)

  return imageUrls ? helpers.getImageUrl(imageUrls, imageTypes) : null
})
const name = computed(() => specialOfferData.value.attributes.name)
const specialOfferId = computed(() => helpers.getIdFromSlugAndId(route.params.slugAndId as string))
const specialOfferKey = computed(() => `special-offer-${specialOfferId.value}`)

const { data, error, status } = await useLazyAsyncData(
  specialOfferKey,
  async () => {
    try {
      const [specialOfferResponse, productsResponse] = await Promise.all([
        $apiHelper.specialOffers.getSpecialOffer(specialOfferId.value!),
        $apiHelper.products.getProducts(getProductsParams())
      ]);

      const { fullPath, path } = route
      const actualPath = helpers.getSpecialOfferPath(specialOfferResponse.data)

      if (path !== actualPath) {
        actualFullPath.value = fullPath.replace(path, actualPath)

        if (import.meta.client) {
          history.replaceState({}, '', actualFullPath.value)
        }
      }

      return { productsResponse, specialOfferResponse }
    } catch (error) {
      showError(error as NuxtError)
    }
  },
  {
    transform: (x) => ({
      productsResponse: JsonApiHelper.denormalizeResponse(x?.productsResponse),
      specialOfferResponse: JsonApiHelper.denormalizeResponse(x?.specialOfferResponse)
    })
  }
)

if (error.value) {
  throw createError(error.value as NuxtError)
}

if (actualFullPath.value) {
  await navigateTo(actualFullPath.value, { redirectCode: 301 })
}

const productsData = computed<ProductData[]>(() => data.value?.productsResponse?.data ?? [])
const specialOfferData = computed<any>(() => data.value?.specialOfferResponse?.data ?? null)

useHead(() => {
  if (!specialOfferData.value) {
    return {}
  }

  const head: Head = {
    title: `${name.value} — iTAB`,
    meta: [{ name: 'description', content: name.value }]
  }

  const previewUrl = getPreviewUrl()

  if (head.meta && previewUrl) {
    head.meta.push(...helpers.getPreviewMetadata(previewUrl))
  }

  return head
})

onMounted(parsePromoCodeFromQuery)

function getPreviewUrl (): string | null {
  const imageUrls = helpers.getImageUrls(specialOfferData.value.relationships.images.data)

  return imageUrls ? helpers.getImageUrl(imageUrls, previewTypes) : null
}

function getProductsParams (): any {
  return {
    'filter[specialOfferId]': specialOfferId.value,
    'page[size]': 24,
    sort: ProductSort.MostPopular
  }
}

async function openCopyLinkModalWindow (link: string): Promise<void> {
  await useModal({ component: ShareLink, attrs: { link } }).open()
}

async function openCopyLinkModalWindowOrCopyLink (): Promise<void> {
  const link = `${constants.siteUrl}${helpers.getSpecialOfferPath(specialOfferData.value)}`

  if (authStore.isUser) {
    await openCopyLinkModalWindow(link)
  } else {
    copyLink(link)
  }
}
</script>

<template>
  <div>
    <Preloader v-if="status === 'pending'" />
    <template v-else-if="status === 'success' && specialOfferData">
      <section>
        <div class="container">
          <Breadcrumbs :current="name" :items="breadcrumbsItems" />

          <div class="content-wrapper">
            <Heading :title="name">
              <template #buttons>
                <BasicButton
                  color="body-background"
                  is-small
                  title="Поделиться"
                  @click="openCopyLinkModalWindowOrCopyLink"
                />
              </template>
            </Heading>

            <div class="overflow-hidden">
              <div class="image-area mb-6">
                <div class="image-wrapper">
                  <img v-if="imageUrl" v-lazy="imageUrl" :alt="name" loading="lazy">
                </div>
              </div>

              <HtmlArea class="html-area" :plain-html="descriptionHtml" />
            </div>
          </div>
        </div>
      </section>

      <section v-if="status === 'success' && productsData.length" class="mt-10">
        <div class="container">
          <div class="h1">
            Товары из&nbsp;акции
          </div>

          <div class="-m-1 flex flex-wrap md:-m-2">
            <div v-for="x in productsData" :key="x.id" class="w-1/2 p-1 md:w-1/3 md:p-2 xl:w-1/5">
              <ProductCard :product-data="x" />
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.image-area {
  float: right;
  margin-left: 24px;

  @media screen and (max-width: 768px) {
    float: initial;
    margin-left: 0;
  }
}

.image-wrapper {
  height: 240px;
  width: 426px;

  @media screen and (max-width: 768px) {
    height: auto;
    width: 100%;
  }

  img {
    border-radius: 12px;
    height: 100%;
    max-width: 100%;
    min-width: 100%;
    object-fit: cover;
    width: 100%;
  }
}

.html-area {
  overflow: initial;
}
</style>
